/**
 * The institution manager's landing screen.
 *
 * A manager's first question is not "how many beds" — it is whether anything
 * needs them today: bookings waiting to be confirmed, a quota about to run out,
 * money that came in. So this leads with those and links out to the screens
 * that act on them.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  PageHeader,
  Panel,
  Spinner,
  StatCard,
} from '../../components/common';
import { useAsync } from '../../hooks/useAsync';
import { PackageCard } from '../hospital/PackageCard';
import { institutionApi, serviceApi } from '../../api/endpoints';
import { formatCurrency } from '../../domain/pricing';
import { isoToDisplayDate, formatTime12, todayKey } from '../../domain/dates';
import {
  INSTITUTION_TYPE_LABELS,
  SERVICE_CATEGORY_LABELS,
  quotaState,
} from '../../domain/institutions';

export function InstitutionOverview() {
  const institution = useAsync(() => institutionApi.mine(), []);
  const bookings = useAsync(() => serviceApi.bookings(), [], { initialData: [] });
  const finance = useAsync(() => institutionApi.finance(), [], { initialData: null });

  const rows = useMemo(() => bookings.data || [], [bookings.data]);

  const pending = useMemo(() => rows.filter((b) => b.status === 'pending'), [rows]);
  const today = useMemo(
    () => rows.filter((b) => b.date === todayKey() && b.status !== 'cancelled'),
    [rows],
  );

  if (institution.error) return <ErrorState error={institution.error} onRetry={institution.reload} />;
  if (institution.loading || !institution.data) return <Spinner label="جاري تحميل بيانات المنشأة..." />;

  const data = institution.data;
  const members = quotaState({ used: data.usage?.members || 0, limit: data.maxSubAccounts });
  const services = quotaState({ used: data.usage?.services || 0, limit: data.maxServices });
  const net = finance.data?.totals?.net ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title={data.name}
        subtitle={`${INSTITUTION_TYPE_LABELS[data.type] || data.type}${data.city ? ` — ${data.city}` : ''}`}
        icon="building-2"
        actions={
          !data.active && (
            <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-300" icon="alert-circle">
              غير مفعّلة — لا تظهر للمرضى
            </Badge>
          )
        }
      />

      {/* What the package gives this institution, and how much of it is used. */}
      <PackageCard />

      {/* An inactive institution is invisible to patients, and nothing on this
          side of the app can change that — so say who can. */}
      {!data.active && (
        <Panel className="border border-amber-500/30 bg-amber-500/[0.05]">
          <div className="flex flex-wrap items-center gap-3">
            <Icon name="alert-circle" size={20} className="shrink-0 text-amber-400" />
            <p className="min-w-0 flex-1 text-sm leading-relaxed text-amber-100/90">
              منشأتك غير مفعّلة حالياً، لذلك لا تظهر خدماتها للمرضى. يمكنك تجهيز الخدمات والحسابات
              الآن — التفعيل يتم من إدارة المنصة.
            </p>
          </div>
        </Panel>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon="calendar-check" label="حجوزات اليوم" value={today.length} accent="cyan" />
        <StatCard icon="clock" label="بانتظار التأكيد" value={pending.length} accent="amber" />
        <StatCard icon="package" label="خدمات معروضة" value={services.used} accent="purple" />
        <StatCard icon="banknote" label="صافي الإيرادات" value={formatCurrency(net)} accent="emerald" />
      </div>

      {/* Quotas */}
      <Panel>
        <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-white">
          <Icon name="gauge" size={18} className="text-cyan-400" />
          حدود الحساب
        </h3>

        <div className="grid gap-4 sm:grid-cols-2">
          <QuotaRow
            label="الأطباء والموظفون"
            quota={members}
            icon="users"
            to="/institution/members"
          />
          <QuotaRow label="الخدمات" quota={services} icon="package" to="/institution/services" />
        </div>

        {(members.full || services.full) && (
          <p className="mt-3 rounded-lg border border-amber-500/20 bg-amber-500/10 p-2.5 text-[11px] leading-relaxed text-amber-200">
            وصلت للحد المسموح. تواصل مع إدارة المنصة لرفعه.
          </p>
        )}
      </Panel>

      {/* Waiting on the manager */}
      <Panel>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="flex items-center gap-2 text-base font-bold text-white">
            <Icon name="clock" size={18} className="text-amber-400" />
            حجوزات بانتظار التأكيد
          </h3>
          <Link to="/institution/bookings">
            <Button variant="ghost" icon="arrow-left">
              كل الحجوزات
            </Button>
          </Link>
        </div>

        {bookings.loading && rows.length === 0 ? (
          <Spinner variant="inline" />
        ) : pending.length === 0 ? (
          <EmptyState
            icon="check-circle-2"
            title="لا توجد حجوزات معلّقة"
            message="كل الحجوزات تمت مراجعتها."
          />
        ) : (
          <ul className="space-y-2">
            {pending.slice(0, 6).map((booking) => (
              <li
                key={booking.id}
                className="flex flex-wrap items-center gap-3 rounded-xl border border-white/[0.07] bg-white/[0.04] p-3"
              >
                <span className="shrink-0 rounded-lg bg-amber-500/10 px-2.5 py-1.5 font-mono text-xs text-amber-300">
                  {formatTime12(booking.time)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-white">{booking.serviceName}</p>
                  <p className="truncate text-[11px] text-slate-400">
                    {booking.patient?.name || 'مريض'} — {isoToDisplayDate(booking.date)}
                  </p>
                </div>
                <Badge className="border-white/10 bg-white/5 text-slate-300">
                  {SERVICE_CATEGORY_LABELS[booking.category] || booking.category}
                </Badge>
                <span className="text-xs font-bold text-emerald-400">
                  {formatCurrency(booking.totalPrice)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

function QuotaRow({ label, quota, icon, to }) {
  const tone = quota.exceeded ? '#f87171' : quota.full ? '#fbbf24' : '#34d399';

  return (
    <Link
      to={to}
      className="block rounded-xl border border-white/5 bg-white/5 p-3 transition-colors hover:border-cyan-500/40"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-xs text-slate-300">
          <Icon name={icon} size={13} style={{ color: tone }} />
          {label}
        </span>
        <span className="text-xs font-bold" style={{ color: tone }}>
          {quota.used} / {quota.limit}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-slate-800">
        <div className="h-full rounded-full" style={{ width: `${quota.percent}%`, background: tone }} />
      </div>
      <p className="mt-1.5 text-[10px] text-slate-500">
        {quota.remaining > 0 ? `يمكنك إضافة ${quota.remaining} بعد` : 'وصلت للحد المسموح'}
      </p>
    </Link>
  );
}

export default InstitutionOverview;
